import { message } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { salesInvoiceService } from '@/services/sales-invoice.service';
import { accountingService } from '@/services/accounting.service';
import { inventoryService } from '@/services/inventory.service';
import { purchaseInvoiceService } from '@/services/purchase-invoice.service';

/**
 * Bóc message BE từ lỗi axios (400 "sau pivot"/validate...) — không crash UI.
 * Envelope chuẩn: `{ success, status, error: { message, code } }` (ưu tiên trước) —
 * fallback về `{ message }` dạng cũ, rồi `(error as Error).message` (axios interceptor
 * `api.ts` đã map từ `error.response.data.error.message`).
 */
function extractErrorMessage(error: unknown, fallback: string): string {
  const data = (
    error as { response?: { data?: { message?: string; error?: { message?: string } } } }
  )?.response?.data;
  const envelopeMessage = data?.error?.message;
  const responseMessage = data?.message;
  return envelopeMessage || responseMessage || (error as Error)?.message || fallback;
}

/**
 * Loại chứng từ kế toán có đồng bộ AMIS. Mỗi loại có bộ route
 * `.../accounting-sync/retry|resync` riêng và query key riêng của màn chi tiết.
 */
export type SyncDocType =
  | 'sales_invoice'
  | 'receipt_voucher'
  | 'goods_receipt'
  | 'goods_issue'
  | 'stock_transfer'
  | 'purchase_invoice';

/**
 * Nơi DUY NHẤT khai "loại chứng từ nào gọi route nào, làm mới query nào". Nối thêm loại
 * chứng từ kế toán mới (phiếu chi, phiếu nhập kho...) chỉ cần thêm một dòng ở đây — đúng
 * ý anh Hưng muốn khối trạng thái đồng bộ thành quy chuẩn dùng chung (TCR-1450).
 */
const SYNC_ACTIONS_BY_DOC_TYPE: Record<
  SyncDocType,
  {
    retry: (id: string) => Promise<unknown>;
    resync: (id: string) => Promise<unknown>;
    listQueryKey: QueryKey;
    detailQueryKey: (id: string) => QueryKey;
  }
> = {
  sales_invoice: {
    retry: (id) => salesInvoiceService.retrySync(id),
    resync: (id) => salesInvoiceService.resync(id),
    listQueryKey: ['sales-invoices'],
    detailQueryKey: (id) => ['accounting', 'sales-invoices', 'detail', id],
  },
  receipt_voucher: {
    retry: (id) => accountingService.retryVoucherSync(id),
    resync: (id) => accountingService.resyncVoucher(id),
    listQueryKey: ['eg_583_transactions'],
    detailQueryKey: (id) => ['eg_583_transaction_detail', id],
  },
  // Ba loại chứng từ kho (TCR-1138). Với chúng, "Đồng bộ lại" chạy lại NGUYÊN đường dispatch
  // phía BE nên xử lý được cả ca chứng từ **chưa hề có task** — ca hỏng phổ biến nhất là mã
  // hàng / mã kho chưa có trên AMIS. Bấm khi chưa sửa xong thì BE trả 400 kèm đúng lý do.
  goods_receipt: {
    retry: (id) => inventoryService.receipts.retrySync(id),
    resync: (id) => inventoryService.receipts.resyncSync(id),
    listQueryKey: ['inventory', 'receipts'],
    detailQueryKey: (id) => ['inventory', 'receipts', id],
  },
  goods_issue: {
    retry: (id) => inventoryService.issues.retrySync(id),
    resync: (id) => inventoryService.issues.resyncSync(id),
    listQueryKey: ['inventory', 'issues'],
    detailQueryKey: (id) => ['inventory', 'issues', id],
  },
  stock_transfer: {
    retry: (id) => inventoryService.transfers.retrySync(id),
    resync: (id) => inventoryService.transfers.resyncSync(id),
    listQueryKey: ['inventory', 'transfers'],
    detailQueryKey: (id) => ['inventory', 'transfers', id],
  },
  // Hoá đơn mua hàng (TCR-2145-S8)
  purchase_invoice: {
    retry: (id) => purchaseInvoiceService.retrySync(id),
    resync: (id) => purchaseInvoiceService.resync(id),
    listQueryKey: ['eg_575_purchase_invoices'],
    detailQueryKey: (id) => ['purchase_invoice_detail', id],
  },
};

/**
 * Type guard kiểm tra một giá trị có phải là SyncDocType mà FE hỗ trợ retry / resync hay không.
 */
export function isSyncDocType(value: unknown): value is SyncDocType {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(SYNC_ACTIONS_BY_DOC_TYPE, value)
  );
}

export interface SyncActionsOptions {
  /**
   * Loại chứng từ bán hàng phái sinh (`sales_discount`, `sales_return`...). Có giá trị thì
   * mutation gọi endpoint `/accounting/sales-documents/:kind/:id/accounting-sync/*`; bỏ
   * trống thì giữ nguyên đường hoá đơn bán hàng `/accounting/sales-invoices/:id/...`.
   */
  documentKind?: string;
  /**
   * Loại chứng từ kế toán. Bỏ trống = `sales_invoice`, giữ nguyên hành vi cũ của mọi lời
   * gọi đang có. `documentKind` (chứng từ bán phái sinh) vẫn được ưu tiên như trước.
   */
  docType?: SyncDocType;
  /**
   * Danh sách query keys bổ sung cần invalidate sau khi retry / resync thành công (AC-4).
   * Ví dụ: khi xem phiếu xuất nhưng retry trên hóa đơn bán liên kết, cần làm mới cả
   * query chi tiết phiếu xuất hiện tại.
   */
  extraQueryKeys?: QueryKey[];
}

/**
 * B9 — mutation "Thử lại" / "Cập nhật & đồng bộ lại" đồng bộ AMIS cho 1 chứng từ
 * (mẫu theo useAmisConnection.ts). `onSuccess` invalidate cả danh sách + chi tiết của
 * đúng loại chứng từ để badge cập nhật ngay; `onError` bóc message BE, không throw tiếp
 * (không crash).
 */
export function useSyncActions(invoiceId: string, options: SyncActionsOptions = {}) {
  const { documentKind, docType = 'sales_invoice', extraQueryKeys } = options;
  const actions = SYNC_ACTIONS_BY_DOC_TYPE[docType];
  const queryClient = useQueryClient();

  const invalidateInvoiceQueries = () => {
    queryClient.invalidateQueries({ queryKey: actions.listQueryKey });
    queryClient.invalidateQueries({ queryKey: actions.detailQueryKey(invoiceId) });
    // Chứng từ phái sinh nằm ở query key riêng của màn chi tiết chứng từ (derived-document-detail).
    if (documentKind) {
      queryClient.invalidateQueries({
        queryKey: ['sales-document-detail', documentKind, invoiceId],
      });
    }
    if (extraQueryKeys?.length) {
      for (const queryKey of extraQueryKeys) {
        queryClient.invalidateQueries({ queryKey });
      }
    }
  };

  const retry = useMutation({
    mutationFn: () =>
      documentKind
        ? salesInvoiceService.retrySalesDocumentSync(documentKind, invoiceId)
        : actions.retry(invoiceId),
    onSuccess: () => {
      message.success('Đã thử lại đồng bộ');
      invalidateInvoiceQueries();
    },
    onError: (error: unknown) => {
      message.error(extractErrorMessage(error, 'Không thể thử lại đồng bộ AMIS'));
    },
  });

  const resync = useMutation({
    mutationFn: () =>
      documentKind
        ? salesInvoiceService.resyncSalesDocument(documentKind, invoiceId)
        : actions.resync(invoiceId),
    onSuccess: () => {
      message.success('Đã cập nhật & đồng bộ lại');
      invalidateInvoiceQueries();
    },
    onError: (error: unknown) => {
      message.error(extractErrorMessage(error, 'Không thể cập nhật & đồng bộ lại AMIS'));
    },
  });

  return { retry, resync };
}
